import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiArrowRight, FiSmartphone, FiBook, FiHeadphones, FiMonitor, FiFileText, FiCheckCircle, FiGlobe, FiEye, FiList, FiType, FiChevronLeft, FiChevronRight, FiPlay, FiSun } = FiIcons;

const formats = [
  { icon: FiBook, t: 'Paperback interior', d: 'Trim-correct pages with running heads, drop-caps, and proper margins — exactly how it prints.' },
  { icon: FiSmartphone, t: 'Kindle / ebook', d: 'Reflowable EPUB preview at any font size — see what Kindle readers see.' },
  { icon: FiMonitor, t: 'In-browser reader', d: 'A clean epub.js reader to flip through chapters before you ship.' },
  { icon: FiHeadphones, t: 'Audiobook', d: 'Square cover + chapter list, ready for ACX / Findaway / Spotify.' },
  { icon: FiFileText, t: 'Print-ready PDF', d: 'PDF/X with bleed and crop marks for KDP Print or IngramSpark.' },
];

const platforms = [
  ['Amazon KDP', 'Ebook + Print'], ['Apple Books', 'EPUB'], ['Kobo', 'EPUB'],
  ['Google Play Books', 'EPUB / PDF'], ['IngramSpark', 'Print PDF'], ['ACX', 'Audiobook'],
];

const checklist = [
  'Trim size + spine + bleed correct', 'Front/back matter in place (copyright, dedication…)',
  'Images ≥ 300 DPI for print', 'EPUB validates (no broken links / images)',
  'Metadata complete (title, subtitle, BISAC, keywords)', 'AI-disclosure + copyright page generated',
];

const BookPreviewerPage = () => {
  const structuredData = { '@context': 'https://schema.org', '@type': 'WebPage', name: 'Book Previewer — ManuscriptSmith', description: 'Preview your finished book across every format and platform — Kindle, paperback, EPUB, audiobook, print PDF — with a publish-readiness check.', url: 'https://manuscriptsmith.com/book-previewer' };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Book Previewer — See Your Book in Every Format Before You Publish | ManuscriptSmith"
        description="Preview your manuscript as a Kindle ebook, paperback interior, EPUB, audiobook and print-ready PDF — across Amazon KDP, Apple Books, Kobo, IngramSpark and ACX — with a publish-readiness checklist."
        keywords="book preview, kindle preview, epub preview, print ready pdf, kdp preview, publish ready checklist, book formats"
        canonicalUrl="https://manuscriptsmith.com/book-previewer"
        structuredData={structuredData}
      />
      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
            <SafeIcon icon={FiEye} className="w-4 h-4" /> Book Previewer
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">See your book the way <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">readers will</span> — everywhere.</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">Before you hit publish, preview your manuscript as a Kindle ebook, a printed paperback, an EPUB, an audiobook and a print-ready PDF — and confirm it's accepted on every store.</p>
          <div className="mt-7"><Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">Preview my book <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" /></Link></div>
        </section>

        {/* Reader visual demonstration */}
        <section className="bg-gradient-to-b from-white to-parchment-50 py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">This is how readers will see your book</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">A clean, distraction-free reader — table of contents, adjustable type, light &amp; dark — plus the exact Kindle reflow and an audiobook player.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 items-stretch">
              {/* In-browser reader */}
              <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden flex flex-col">
                <div className="flex items-center gap-2 px-4 h-10 border-b border-gray-100 bg-parchment-50">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-300" /><span className="w-2.5 h-2.5 rounded-full bg-amber-300" /><span className="w-2.5 h-2.5 rounded-full bg-green-300" />
                  <span className="flex-1 text-center text-[11px] text-gray-400 truncate">Echo: Love for Kimball — ManuscriptSmith Reader</span>
                  <span className="inline-flex items-center gap-1 text-gray-400"><SafeIcon icon={FiType} className="w-3.5 h-3.5" /><SafeIcon icon={FiSun} className="w-3.5 h-3.5" /></span>
                </div>
                <div className="grid grid-cols-3 flex-1">
                  <div className="col-span-1 border-r border-gray-100 p-3 bg-parchment-50/40 hidden sm:block">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2"><SafeIcon icon={FiList} className="w-3 h-3" /> Contents</div>
                    {['Prologue','Chapter One','Chapter Two','Chapter Three','Chapter Four'].map((c, i) => (
                      <div key={c} className={`text-[12px] px-2 py-1 rounded ${i === 3 ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-500'}`}>{c}</div>
                    ))}
                  </div>
                  <div className="col-span-3 sm:col-span-2 p-6" style={{ fontFamily: 'Georgia, serif' }}>
                    <div className="text-[10px] uppercase tracking-widest text-primary-500 mb-1">Chapter Three</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">The Doorway</h3>
                    <p className="text-[15px] leading-loose text-gray-800">
                      <span className="float-left text-5xl leading-none pr-2 pt-1 font-bold text-primary-600">T</span>
                      heo arrived the way weather does — without asking — and the whole morning she had rehearsed
                      collapsed into a single held breath. The rain kept its own time against the glass, patient as
                      memory, while the kettle forgot itself on the stove.
                    </p>
                  </div>
                </div>
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-3 text-[11px] text-gray-400">
                  <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full w-[42%] bg-gradient-to-r from-primary-500 to-secondary-500" /></div>
                  <span className="whitespace-nowrap">Chapter 3 of 12 · 42%</span>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                </div>
              </div>

              {/* Kindle e-reader + audiobook */}
              <div className="flex flex-col gap-6">
                <div className="mx-auto w-full max-w-[230px] rounded-[2rem] border-[10px] border-gray-900 bg-gray-900 shadow-xl">
                  <div className="rounded-[1.2rem] bg-[#f7f3ea] p-4 h-[300px] flex flex-col" style={{ fontFamily: 'Georgia, serif' }}>
                    <div className="text-[9px] uppercase tracking-widest text-gray-400 mb-2">Kindle preview · reflowable</div>
                    <p className="text-[13px] leading-relaxed text-gray-800 flex-1">
                      She heard him before she saw him: the door, the rain, the old apology of his footsteps. Every
                      careful sentence she had rehearsed left her at once, and what remained was only the truth of
                      the doorway and the weather coming through it.
                    </p>
                    <div className="text-[9px] text-gray-400 flex justify-between border-t border-gray-200 pt-1.5 mt-2"><span>Location 1,204</span><span>38%</span></div>
                  </div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-900 text-white p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0"><SafeIcon icon={FiHeadphones} className="w-6 h-6 text-white" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">Echo: Love for Kimball</div>
                    <div className="flex items-end gap-0.5 h-5 mt-1.5">
                      {[...Array(28)].map((_, i) => <span key={i} className="w-1 rounded-full bg-gradient-to-t from-primary-500 to-secondary-400" style={{ height: `${30 + ((i * 7) % 70)}%` }} />)}
                    </div>
                  </div>
                  <span className="w-9 h-9 rounded-full bg-white text-gray-900 flex items-center justify-center flex-shrink-0"><SafeIcon icon={FiPlay} className="w-4 h-4 ml-0.5" /></span>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400 mt-6">Preview shown with sample text — your book renders in your chosen theme and fonts.</p>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Every format, from one manuscript</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {formats.map((f) => (
                <div key={f.t} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <SafeIcon icon={f.icon} className="w-6 h-6 text-secondary-600 mb-3" />
                  <div className="font-semibold text-gray-800">{f.t}</div>
                  <div className="text-sm text-gray-500 mt-1">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8"><SafeIcon icon={FiGlobe} className="w-8 h-8 text-primary-500 mx-auto mb-3" /><h2 className="text-3xl font-bold text-gray-900">Ready for every platform</h2></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {platforms.map(([p, kind]) => (
                <div key={p} className="rounded-xl border border-gray-200 bg-white p-4 text-center"><div className="font-semibold text-gray-800 text-sm">{p}</div><div className="text-[11px] text-gray-400">{kind}</div></div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Publish-readiness — checked for you</h2>
            <div className="rounded-2xl border border-primary-200 bg-parchment-50 p-6">
              <ul className="space-y-2.5">
                {checklist.map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-gray-700"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {c}</li>
                ))}
              </ul>
              <div className="mt-4 text-xs text-gray-500">Export unlocks only when the checks pass — so your upload isn't rejected.</div>
            </div>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Know it's ready — then ship it.</h2>
          <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">Get started <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" /></Link>
        </section>
      </main>
    </div>
  );
};

export default BookPreviewerPage;
